import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { message } from "antd";
import {
	FileCheck,
	ClipboardList,
	ShieldAlert,
	Eye,
	Wrench,
	CircleCheck,
	CircleX,
	OctagonAlert,
	TriangleAlert,
	Download,
	Share2,
	RotateCcw,
	Lock
} from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import PlanReviewFindingModal from "components/plan-review-finding-modal/plan-review-finding-modal";
import PlanReviewPageHeader, { PlanReviewHeaderTone } from "components/plan-review-page-header/plan-review-page-header";
import OllieAvatar, { ollieMoodForOutcome } from "components/ollie-avatar/ollie-avatar";
import PlanReviewLoadError from "components/plan-review-load-error/plan-review-load-error";
import PlanReviewApprovalAcknowledgmentModal, {
	isAhjSubmissionAcknowledgmentRequired
} from "components/plan-review-approval-acknowledgment-modal/plan-review-approval-acknowledgment-modal";
import PlanReviewFindingHistory from "components/plan-review-finding-history/plan-review-finding-history";
import PlanReviewOllieOriginal from "components/plan-review-ollie-original/plan-review-ollie-original";
import PlanReviewApplicantFeedbackCard from "components/plan-review-applicant-feedback-card/plan-review-applicant-feedback-card";
import { PlanReviewFindingHistoryCount } from "components/plan-review-finding-review-marks/plan-review-finding-review-marks";
import PlanReviewMarkedIncorrectGroup from "components/plan-review-marked-incorrect-group/plan-review-marked-incorrect-group";

import { useGetPlanReviewJobSubscription } from "graphql/subscriptions/plan-review-job-by-id.generated";
import { useGetPlanReviewFindingsQuery } from "graphql/queries/get-plan-review-findings.generated";
import { useCreatePlanReviewCheckoutMutation } from "graphql/mutations/create-plan-review-checkout.generated";
import usePagePdfExport from "hooks/usePagePdfExport";
import { usePlanReviewFlowStore } from "state/zustand-stores/plan-review-flow-store";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { buildReportFileName } from "utils/page-pdf";
import { getPropertyAddress } from "utils/helpers";
import routeNames from "constants/route-names";
import {
	PlanReviewFindingStatus,
	PlanReviewReviewOutcome,
	isAuthorityRole,
	isBlockingPlanReviewFindingStatus
} from "constants/field-values";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildPlanReviewJobDescription } from "utils/ollie-screen-description";
import {
	buildFindingHistory,
	changedFindingFields,
	findingSeverityLabel,
	findingStatusLabel,
	formatPersonWithOrganization,
	formatReviewDateTime,
	isFindingEditedByReviewer,
	isFindingRevertedToOllie,
	isMarkedIncorrectByApplicant,
	isOllieStatusBlocking,
	latestApplicantFeedback,
	latestReviewerChange,
	ollieValuesOf
} from "utils/plan-review-finding-review";

type Job = NonNullable<
	NonNullable<ReturnType<typeof useGetPlanReviewJobSubscription>["data"]>["plan_review_jobs_by_pk"]
>;
// Not embedded in the job subscription any more — see plan-review-details.tsx's Finding
// type comment (MWA-788: the free tier is two counts, never a per-finding row).
type FindingsResult = NonNullable<ReturnType<typeof useGetPlanReviewFindingsQuery>["data"]>["get_plan_review_findings"];
type Finding = FindingsResult["findings"][number];

const OUTCOME_LABELS: Record<string, { label: string; tone: PlanReviewHeaderTone }> = {
	[PlanReviewReviewOutcome.APPROVED]: { label: "Approved", tone: "approved" },
	[PlanReviewReviewOutcome.REVISIONS_NEEDED]: { label: "Revisions Needed", tone: "revisions" }
};

// What each flagged status is called on a page written for the applicant.
const FINDING_STATUS_META: Record<string, { label: string; className: string; icon: typeof CircleCheck }> = {
	[PlanReviewFindingStatus.FAIL]: { label: "Revisions Needed", className: "prcl-status-fail", icon: CircleX },
	[PlanReviewFindingStatus.MISSING]: { label: "Missing Content", className: "prcl-status-fail", icon: OctagonAlert },
	[PlanReviewFindingStatus.WARNING]: {
		label: "Revisions Needed",
		className: "prcl-status-warning",
		icon: TriangleAlert
	}
};

// Where a category with no row of its own sorts: after every category the jurisdiction
// still keeps, in the order the analysis happened to name them.
const UNCATEGORISED_ORDER = 999;

const isFlagged = (finding: Finding): boolean =>
	finding.status === PlanReviewFindingStatus.FAIL ||
	finding.status === PlanReviewFindingStatus.MISSING ||
	finding.status === PlanReviewFindingStatus.WARNING;

// A finding gets an item number when Ollie or the reviewer flagged it. The numbers do not change
// when the reviewer clears an item, so "Item #3" means the same finding for everyone.
const hasItemNumber = (finding: Finding): boolean => isFlagged(finding) || isOllieStatusBlocking(finding);

// Citations of a finding as text, for the Reviewer adjustments section.
const formatCodeSectionLabels = (references: { citation_text?: string | null }[]): string =>
	references
		.map(reference => reference.citation_text)
		.filter(Boolean)
		.join(", ") || "None";

/** One line of the Reviewer adjustments section for each field that the reviewer changed. */
const describeReviewerAdjustments = (finding: Finding): string[] => {
	const changedFields = changedFindingFields(finding);
	const ollieValues = ollieValuesOf(finding);
	const lines: string[] = [];
	if (changedFields.status) {
		lines.push(`Status: ${findingStatusLabel(ollieValues.status)} → ${findingStatusLabel(finding.status)}`);
	}
	if (changedFields.severity) {
		lines.push(
			`Severity: ${findingSeverityLabel(ollieValues.severity) ?? "None"} → ${findingSeverityLabel(finding.severity) ?? "None"}`
		);
	}
	if (changedFields.summary) {
		lines.push(`Observation: “${ollieValues.summary}” → “${finding.summary}”`);
	}
	if (changedFields.correctiveAction) {
		lines.push(`Recommended Action: “${ollieValues.correctiveAction || "None"}” → “${finding.corrective_action || "None"}”`);
	}
	if (changedFields.codeSections) {
		lines.push(
			`Code Sections: ${formatCodeSectionLabels(finding.requirement?.plan_review_requirement_code_sections ?? [])} → ${formatCodeSectionLabels(finding.code_sections)}`
		);
	}
	return lines;
};

// Left accent of a card: indigo when the reviewer changed the finding, amber when it only has
// feedback in its history.
const commentCardAccentClass = (isEdited: boolean, hasHistory: boolean): string => {
	if (isEdited) return " prcl-card-edited";
	if (hasHistory) return " prcl-card-history";
	return "";
};

/**
 * One item of the comment letter. Shows the current values, marks the fields that the AHJ reviewer
 * changed, and the latest applicant feedback. A finding that the applicant marked as Incorrect is
 * shown muted.
 */
const CommentLetterCard = ({
	finding,
	itemNumber,
	isReviewer,
	onOpenFinding
}: {
	finding: Finding;
	itemNumber: number;
	isReviewer: boolean;
	onOpenFinding: (findingId: string) => void;
}) => {
	const meta = FINDING_STATUS_META[finding.status ?? ""] ?? FINDING_STATUS_META[PlanReviewFindingStatus.WARNING];
	const StatusIcon = meta.icon;
	const requirement = finding.requirement?.description || finding.requirement?.title;
	const correctiveAction =
		finding.corrective_action && finding.corrective_action !== "null" ? finding.corrective_action : null;

	const isEdited = isFindingEditedByReviewer(finding);
	const isReverted = isFindingRevertedToOllie(finding);
	const reviewerChange = latestReviewerChange(finding);
	const applicantFeedback = latestApplicantFeedback(finding);
	const history = buildFindingHistory(finding, { includeReviewerFeedback: isReviewer });
	const hasHistory = history.length > 1;
	const changedFields = changedFindingFields(finding);
	const ollieValues = ollieValuesOf(finding);
	const isObservationEdited = changedFields.summary;
	const isCorrectiveActionEdited = changedFields.correctiveAction;
	const severityLabel = findingSeverityLabel(finding.severity);

	const accentClassName = commentCardAccentClass(isEdited, hasHistory);
	const mutedClassName = isMarkedIncorrectByApplicant(finding) ? " prcl-card-muted" : "";

	return (
		<article className={`prcl-card${accentClassName}${mutedClassName}`}>
			<header className="prcl-card-head">
				<div className="prcl-card-heading">
					<span className="prcl-card-number">{itemNumber}.</span>
					<div>
						<p className="prcl-card-category">{finding.category_record?.name ?? finding.category ?? "Other"}</p>
						<h3 className="prcl-card-title">{finding.title}</h3>
						{isEdited && reviewerChange && (
							<p className="prcl-card-updated">
								Updated by {formatPersonWithOrganization(reviewerChange.user_name, reviewerChange.organization_name)}{" "}
								· {formatReviewDateTime(reviewerChange.created_at)}
							</p>
						)}
						{isReverted && <p className="prcl-card-updated">Reverted to Ollie’s original</p>}
					</div>
				</div>
				<div className="prcl-card-badges">
					{hasHistory && <PlanReviewFindingHistoryCount count={history.length} label="updates" />}
					<span className={`prcl-status ${meta.className}`}>
						<StatusIcon size={14} />
						{meta.label}
					</span>
					{severityLabel && <span className="prcl-severity">{severityLabel}</span>}
				</div>
			</header>

			<div className="prcl-card-body">
				{requirement && (
					<section className="prcl-block">
						<p className="prcl-block-label">
							<ShieldAlert size={14} />
							What is required
						</p>
						<div className="prcl-block-text">{requirement}</div>
					</section>
				)}

				{finding.summary && (
					<section className="prcl-block">
						<div className="prcl-block-head">
							<p className="prcl-block-label">
								<Eye size={14} />
								What Ollie found{isObservationEdited ? " *" : ""}
							</p>
							{isObservationEdited && (
								<PlanReviewOllieOriginal fieldLabel="Observation" originalValue={ollieValues.summary} />
							)}
						</div>
						<div className="prcl-block-text">{finding.summary}</div>
					</section>
				)}

				{correctiveAction && (
					<section className="prcl-block">
						<div className="prcl-block-head">
							<p className="prcl-block-label prcl-block-label-fix">
								How to fix this{isCorrectiveActionEdited ? " *" : ""}
							</p>
							{isCorrectiveActionEdited && (
								<PlanReviewOllieOriginal
									fieldLabel="Recommended Action"
									originalValue={ollieValues.correctiveAction}
								/>
							)}
						</div>
						<div className="prcl-block-text prcl-block-fix">
							<Wrench size={16} />
							<span>{correctiveAction}</span>
						</div>
					</section>
				)}

				{(isObservationEdited || isCorrectiveActionEdited) && (
					<p className="prcl-footnote">
						* Adjusted by {reviewerChange?.user_name || "Reviewer"}; see Reviewer adjustments below.
					</p>
				)}

				{applicantFeedback && (
					<div className="prcl-applicant-feedback">
						<PlanReviewApplicantFeedbackCard feedback={applicantFeedback} appearance="plain" />
					</div>
				)}
			</div>

			<footer className="prcl-card-foot" data-pdf-hide>
				<PlanReviewFindingHistory
					entries={history}
					rightAction={
						<button type="button" className="prcl-details-btn" onClick={() => onOpenFinding(finding.id)}>
							<Eye size={16} />
							View Full Details &amp; Citations
						</button>
					}
				/>
			</footer>
		</article>
	);
};

/**
 * The comment letter of one submittal: every item the applicant has to answer, and
 * nothing else.
 *
 * The report screen next door is written for the reviewer — it carries the passing
 * checks, the document list and the analysis around them. This page is what gets
 * handed to whoever has to do the work, so each item states the rule, what Ollie saw
 * and what to change, with the full finding one click away.
 */
const PlanReviewComments = () => {
	const navigate = useNavigate();
	const params = useParams<{ planReviewId: string; jobId: string }>();
	const jobId = params.jobId as string;
	const planReviewId = params.planReviewId as string;
	const { loggedUserValue } = useLoggedUser();
	const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
	// Findings marked Incorrect by the applicant are listed in their own group at the end, unless
	// the reader asks to see them in their normal place too.
	const [showMarkedIncorrectInPlace, setShowMarkedIncorrectInPlace] = useState(false);
	const setSelection = usePlanReviewFlowStore(state => state.setSelection);
	const { exportRef, isExporting, exportToPdf } = usePagePdfExport<HTMLDivElement>();

	const { data, loading, error } = useGetPlanReviewJobSubscription({
		skip: !jobId,
		variables: { id: jobId },
		onData: data => {
			console.log("useGetPlanReviewJobSubscription data:", data);
		}
	});
	const job = data?.plan_review_jobs_by_pk;

	const { data: findingsData } = useGetPlanReviewFindingsQuery({
		skip: !jobId,
		variables: { jobId },
		fetchPolicy: "cache-and-network"
	});
	const findings: Finding[] = useMemo(() => findingsData?.get_plan_review_findings.findings ?? [], [findingsData]);
	const isLocked = findingsData?.get_plan_review_findings.is_locked ?? false;
	const passedCount = findingsData?.get_plan_review_findings.passed_count ?? 0;
	const needsAttentionCount = findingsData?.get_plan_review_findings.needs_attention_count ?? 0;

	const [createPlanReviewCheckout] = useCreatePlanReviewCheckoutMutation();
	const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);
	const handleUnlock = useCallback(async () => {
		if (!job?.plan_review_id) return;
		setIsRedirectingToCheckout(true);
		try {
			const result = await createPlanReviewCheckout({
				variables: {
					input: {
						plan_review_id: job.plan_review_id,
						cycle_number: null,
						success_url: window.location.href,
						cancel_url: window.location.href
					}
				}
			});
			const checkoutUrl = result.data?.create_plan_review_checkout?.checkout_url;
			if (!checkoutUrl) throw new Error("Could not start checkout.");
			window.location.href = checkoutUrl;
		} catch (checkoutError) {
			console.error("Create plan review checkout failed:", checkoutError);
			message.error("We couldn't start checkout. Please try again.");
			setIsRedirectingToCheckout(false);
		}
	}, [job?.plan_review_id, createPlanReviewCheckout]);

	// Tells Ollie which submittal's comments are on screen.
	useDescribeScreenToOllie(
		buildPlanReviewJobDescription({
			isCommentsView: true,
			jobId: job?.id,
			planReviewId: job?.plan_review_id,
			submissionTypeId: job?.plan_review?.submission_type?.id,
			submissionNumber: job?.submission_number,
			propertyName: job?.plan_review?.property?.name,
			jurisdictionName: job?.plan_review?.jurisdiction_record?.name,
			submissionTypeName: job?.plan_review?.submission_type?.name,
			reviewType: job?.plan_review?.review_type,
			state: job?.state,
			reviewOutcome: job?.review_outcome,
			findingCount: needsAttentionCount,
			propertyId: job?.plan_review?.property_id,
			jurisdictionId: job?.plan_review?.jurisdiction_id,
			organizationId: job?.plan_review?.organization_id,
			workspaceId: job?.plan_review?.workspace_id,
			createdByUserId: job?.plan_review?.created_by_user_id
		}),
		{ propertyId: job?.plan_review?.property_id, jurisdictionId: job?.plan_review?.jurisdiction_id }
	);


	// The letter reads a category at a time, in the jurisdiction's own order.
	//
	// Grouped by the category's name rather than by its row: the analysis writes the name
	// on every finding but only links the row while the office still has that category, so
	// ordering by the link alone would split one category in two — half of it in place and
	// half of it at the end. A category keeps the earliest position any of its findings
	// knows about; inside it they stay in the order the report ranked them.
	const numberedFindings = useMemo(() => {
		const flagged = findings.filter(hasItemNumber);

		const groups = new Map<string, { order: number; findings: Finding[] }>();
		for (const finding of flagged) {
			const name = finding.category_record?.name ?? finding.category ?? "Other";
			const order = finding.category_record?.sort_order ?? UNCATEGORISED_ORDER;
			const group = groups.get(name);
			if (group) {
				group.findings.push(finding);
				group.order = Math.min(group.order, order);
			} else {
				groups.set(name, { order, findings: [finding] });
			}
		}

		return [...groups.entries()]
			.sort(([nameA, a], [nameB, b]) => a.order - b.order || nameA.localeCompare(nameB))
			.flatMap(([, group]) => group.findings);
	}, [findings]);

	// The items that still need a revision, in letter order. A finding that the reviewer moved to Pass
	// or Not Applicable leaves the list but keeps its number.
	const items = useMemo(() => numberedFindings.filter(isFlagged), [numberedFindings]);
	const itemNumberByFindingId = useMemo(
		() => new Map(numberedFindings.map((finding, index) => [finding.id, index + 1])),
		[numberedFindings]
	);

	// Looked up in every finding, not only in the items: the modal stays open on a finding that
	// the reviewer has just moved out of the list.
	const selectedFinding = useMemo(
		() => findings.find(finding => finding.id === selectedFindingId) ?? null,
		[findings, selectedFindingId]
	);

	const selectedFindingDocument = useMemo(() => {
		if (!selectedFinding?.document_id) return null;
		return (job?.plan_review_documents ?? []).find(d => d.id === selectedFinding.document_id) ?? null;
	}, [job, selectedFinding]);

	const selectedIndex = selectedFindingId ? items.findIndex(finding => finding.id === selectedFindingId) : -1;
	const previousFindingId = selectedIndex > 0 ? items[selectedIndex - 1].id : null;
	const nextFindingId = selectedIndex >= 0 && selectedIndex < items.length - 1 ? items[selectedIndex + 1].id : null;

	// The upload screen starts empty: the revision is only what the applicant uploads in
	// answer to these comments, and this submittal keeps the package it was reviewed on.
	const handleResubmit = useCallback(() => {
		if (!job) return;
		const planReview = job.plan_review;
		setSelection({
			organizationId: planReview?.organization_id ?? "",
			propertyId: planReview?.property_id ?? "",
			jurisdictionId: planReview?.jurisdiction_id ?? "",
			submissionTypeId: planReview?.submission_type?.id ?? "",
			propertyName: planReview?.property?.name ?? ""
		});
		navigate(routeNames.PLAN_REVIEW_COLLECTION_RESUBMIT.replace(":planReviewId", job.plan_review_id ?? planReviewId));
	}, [job, planReviewId, setSelection, navigate]);

	if (loading && !job) return <LoadingOverlay />;

	// Without the submittal every block below renders empty — down to "no revisions were
	// requested", which is a statement about the review and not about the read that
	// failed. Say what actually happened instead.
	if (!job) return <PlanReviewLoadError subject="this submittal" notFound={!error} />;

	const outcome = job?.review_outcome ?? PlanReviewReviewOutcome.REVISIONS_NEEDED;
	const outcomeMeta = OUTCOME_LABELS[outcome] ?? OUTCOME_LABELS[PlanReviewReviewOutcome.REVISIONS_NEEDED];

	// What the reviewer changed on this letter.
	const isReviewer = isAuthorityRole(loggedUserValue?.role);
	const isLatestSubmittal = job?.plan_review?.latest_submittal?.[0]?.id === job?.id;
	const isResultAdjustedByReviewer = findings.some(
		finding => isOllieStatusBlocking(finding) !== isBlockingPlanReviewFindingStatus(finding.status)
	);
	const statusLabel = {
		...outcomeMeta,
		label: isResultAdjustedByReviewer ? `${outcomeMeta.label} · adjusted by reviewer` : outcomeMeta.label
	};
	const adjustedFindings = numberedFindings.filter(isFindingEditedByReviewer);
	const markedIncorrectItems = items.filter(isMarkedIncorrectByApplicant);
	const listedItems = showMarkedIncorrectInPlace ? items : items.filter(finding => !isMarkedIncorrectByApplicant(finding));
	const propertyName = job?.plan_review?.property?.name || "this property";
	const reviewDate = job?.analysis_completed_at ?? job?.created_at;

	// The PDF is a capture of this page, which is already the letter: every item is laid
	// out in full, so there is nothing to expand first. The file name follows the report's
	// so the two exports of one submittal sort together, and says which of the two it is.
	const handleExport = () =>
		exportToPdf(
			buildReportFileName(
				job?.plan_review?.property?.name || "Plan Review",
				"Comment Letter",
				`Plan Review ${planReviewId}`,
				`Submission ${jobId}`
			)
		);

	const mustAcknowledgeAhjSubmission = isAhjSubmissionAcknowledgmentRequired({
		reviewOutcome: job?.review_outcome,
		userAcknowledgments: job?.ahj_submission_acknowledgments,
		userRole: loggedUserValue.role
	});

	return (
		<>
			<OllieLayout>
				<div className={`prcl-shell${isExporting ? " prcl-shell-exporting" : ""}`} ref={exportRef}>
					<PlanReviewPageHeader
						propertyName={job?.plan_review?.property?.name}
						address={getPropertyAddress(
							job?.plan_review?.property?.address,
							job?.plan_review?.property?.standardized_address
						)}
						submittedLabel={job?.created_at ? `Submitted ${dayjs(job.created_at).format("MMMM D, YYYY")}` : null}
						status={statusLabel}
						submissionType={job?.plan_review?.submission_type}
						ahjName={job?.plan_review?.jurisdiction_record?.name}
						ollieId={job?.plan_review?.ollie_id}
					>
						{/* Sharing this page with the applicant is its own ticket; hidden until then. */}
						<button className="prph-btn-secondary" onClick={() => undefined} hidden>
							<Share2 size={16} />
							Share
						</button>
						<button className="prph-btn-secondary" onClick={handleExport} disabled={isExporting}>
							<Download size={16} />
							{isExporting ? "Exporting…" : "Export"}
						</button>
						<button className="prph-btn-primary" onClick={handleResubmit}>
							<RotateCcw size={16} />
							Submit Revisions
						</button>
					</PlanReviewPageHeader>

					<div className="prcl-page">
						<div className="prcl-content">
							<div className="prcl-intro">
								<div className="prcl-intro-main">
									<div className="prcl-intro-avatar">
										<OllieAvatar mood={ollieMoodForOutcome(outcome)} />
									</div>
									<p className="prcl-intro-text">
										Hi! I&apos;m Ollie. I&apos;ve reviewed your plan submittal for <strong>{propertyName}</strong>.
										{isLocked ? (
											<> Pay the Ollie pre-screening fee below to see what needs a revision, and how to fix it.</>
										) : items.length > 0 ? (
											<>
												{" "}
												Before these plans can be approved, please review and address the{" "}
												<span className="prcl-intro-count">
													{items.length} {items.length === 1 ? "item" : "items"}
												</span>{" "}
												below. Once you&apos;ve made the necessary updates, you can upload your revised plans directly
												from this page.
											</>
										) : (
											<> Nothing on this submittal needs a revision right now.</>
										)}
									</p>
								</div>

								<div className="prcl-intro-facts">
									<span className="prcl-intro-fact">
										<ClipboardList size={16} />
										<span className="prcl-intro-label">Review Date:</span>
										{reviewDate ? dayjs(reviewDate).format("MMMM D, YYYY") : "—"}
									</span>
									<span className="prcl-dot">•</span>
									<span className="prcl-intro-fact">
										<span className="prcl-intro-label">Passed:</span>
										<span className="prcl-intro-total">{passedCount}</span>
									</span>
									<span className="prcl-dot">•</span>
									<span className="prcl-intro-fact">
										<span className="prcl-intro-label">Needs Attention:</span>
										<span className={`prcl-intro-total${needsAttentionCount > 0 ? " prcl-intro-total-open" : ""}`}>
											{needsAttentionCount}
										</span>
									</span>
								</div>
							</div>

							{isLocked && (
								<div className="prd-paywall">
									<Lock size={18} className="prd-paywall-icon" />
									<div className="prd-paywall-copy">
										<p className="prd-paywall-title">Unlock how to fix these items</p>
										<p className="prd-paywall-text">
											Pay the Ollie pre-screening fee to see the fix for each item below, and to unlock resubmittals for
											this project.
										</p>
									</div>
									<button className="prph-btn-primary" onClick={handleUnlock} disabled={isRedirectingToCheckout}>
										{isRedirectingToCheckout ? "Redirecting…" : "Unlock"}
									</button>
								</div>
							)}

							{!isLocked && (
							<>
							<h2 className="prcl-section-title">
								<FileCheck size={20} className="prcl-section-icon" />
								Revisions Required
							</h2>

							{items.length === 0 && (
								<div className="prcl-empty">
									<CircleCheck size={20} />
									<p>No revisions were requested for this submittal.</p>
								</div>
							)}

							<div className="prcl-list">
								{listedItems.map(finding => (
									<CommentLetterCard
										key={finding.id}
										finding={finding}
										itemNumber={itemNumberByFindingId.get(finding.id) ?? 0}
										isReviewer={isReviewer}
										onOpenFinding={setSelectedFindingId}
									/>
								))}
								<PlanReviewMarkedIncorrectGroup
									count={markedIncorrectItems.length}
									isShownInPlace={showMarkedIncorrectInPlace}
									onToggleShownInPlace={() => setShowMarkedIncorrectInPlace(shown => !shown)}
								>
									{markedIncorrectItems.map(finding => (
										<CommentLetterCard
											key={finding.id}
											finding={finding}
											itemNumber={itemNumberByFindingId.get(finding.id) ?? 0}
											isReviewer={isReviewer}
											onOpenFinding={setSelectedFindingId}
										/>
									))}
								</PlanReviewMarkedIncorrectGroup>
								<section className="prcl-adjustments">
									<div className="prcl-adjustments-head">
										<h3 className="prcl-adjustments-title">Reviewer adjustments</h3>
										<p className="prcl-adjustments-description">
											Adjustments made by the authority reviewer, including items removed from the comment letter body or
											updated with footnotes.
										</p>
									</div>
									<div className="prcl-adjustments-list">
										{adjustedFindings.length > 0 ? (
											adjustedFindings.map(finding => {
												const reviewerChange = latestReviewerChange(finding);
												return (
													<div key={finding.id} className="prcl-adjustment">
														<div className="prcl-adjustment-title">
															<span>
																Item #{itemNumberByFindingId.get(finding.id)} · {finding.title}
															</span>
															{reviewerChange && (
																<span className="prcl-adjustment-meta">
																	{formatPersonWithOrganization(
																		reviewerChange.user_name,
																		reviewerChange.organization_name
																	)}{" "}
																	· {new Date(reviewerChange.created_at).toLocaleDateString()}
																</span>
															)}
														</div>
														{describeReviewerAdjustments(finding).map(line => (
															<p key={line} className="prcl-adjustment-line">
																{line}
															</p>
														))}
													</div>
												);
											})
										) : (
											<p className="prcl-adjustment-line">No reviewer adjustments recorded for this submittal.</p>
										)}
									</div>
								</section>
							</div>
							</>
							)}
						</div>
					</div>
				</div>
			</OllieLayout>

			<PlanReviewFindingModal
				isOpen={!!selectedFinding}
				onClose={() => setSelectedFindingId(null)}
				item={selectedFinding}
				isLatestSubmittal={isLatestSubmittal}
				submissionNumber={job?.submission_number}
				jurisdictionId={job?.plan_review?.jurisdiction_id}
				document={selectedFindingDocument}
				onPrevious={() => previousFindingId && setSelectedFindingId(previousFindingId)}
				onNext={() => nextFindingId && setSelectedFindingId(nextFindingId)}
				hasPrevious={!!previousFindingId}
				hasNext={!!nextFindingId}
			/>

			{mustAcknowledgeAhjSubmission && (
				<PlanReviewApprovalAcknowledgmentModal
					jobId={jobId}
					ollieId={job?.plan_review?.ollie_id}
					propertyName={job?.plan_review?.property?.name}
					jurisdictionName={job?.plan_review?.jurisdiction_record?.name}
					jurisdictionWebsiteUrl={job?.plan_review?.jurisdiction_record?.website_url}
				/>
			)}
		</>
	);
};

export default PlanReviewComments;
